import { ComponentType } from 'react'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import {
   PlanSelection,
   FubConnect,
   PaymentDetails,
   TeamInvitation,
   LeadsAvailability,
   UserAvailability,
   BusinessDetails,
   CnameSetup,
   PresentationStep,
   SummaryBeforeStaging,
   StageMappingNurtureLeads,
   StageMappingAppointmentStages,
   StageMappingStopStages,
   StageMappingDefinition,
   CampaignConfiguration,
   VoiceAssistant,
   PhoneNumberConfiguration,
   SetupInBoxApp,
   PricingStepV2,
   DnDStageMappingOnboarding,
} from './index'
import { CalendarConnection } from './steps/CalendarConnection'

/**
 * Step Registry Pattern
 *
 * Centralized mapping of onboarding steps to their respective components.
 * This pattern provides several benefits:
 *
 * 1. **Scalability**: Easy to add/remove/reorder steps
 * 2. **Maintainability**: All step mappings in one place
 * 3. **Type Safety**: TypeScript ensures all steps are mapped
 * 4. **Discoverability**: Clear overview of all available steps
 * 5. **Testability**: Can mock individual steps easily
 *
 * @example
 * ```tsx
 * const StepComponent = STEP_REGISTRY[currentStep]
 * return StepComponent ? <StepComponent /> : null
 * ```
 */
export const STEP_REGISTRY: Record<TOnboardingStep, ComponentType> = {
   'plan-selection': PricingStepV2,
   'fub-connect': FubConnect,
   'payment-details': PaymentDetails,
   'team-invitation': TeamInvitation,
   'calendar-connection': CalendarConnection,
   'leads-availability': LeadsAvailability,
   'user-availability': UserAvailability,
   'business-details': BusinessDetails,
   'assistant-name': PlanSelection, // TODO: Create AssistantName component
   'setup-cname': CnameSetup,
   'presentation-step': PresentationStep,
   'summary-before-stage-mapping': SummaryBeforeStaging,
   'stage-mapping-new-leads': StageMappingNurtureLeads, // TODO: Create separate component if needed
   'stage-mapping-nurture-stages': StageMappingNurtureLeads,
   'stage-mapping-appointment': StageMappingAppointmentStages,
   'stage-mapping-stop-stages': StageMappingStopStages,
   'stage-mapping-definition': StageMappingDefinition,
   'campaign-configuration': CampaignConfiguration,
   'voice-agent-configuration': VoiceAssistant,
   'phone-number-configuration': PhoneNumberConfiguration,
   'install-inbox-app': SetupInBoxApp,
   'dnd-stage-mapping': DnDStageMappingOnboarding,
}

/**
 * Helper function to get a step component by step name.
 * Provides null-safety and easier testing.
 *
 * @param step - The onboarding step name
 * @returns The component for the given step, or null if not found
 */
export const getStepComponent = (
   step: TOnboardingStep
): ComponentType | null => {
   return STEP_REGISTRY[step] ?? null
}
